import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import { api } from '../services/api';
import {
  Box,
  Container,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon
} from '@mui/icons-material';

function EditChild() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    date_of_birth: '',
    gender: '',
    grade: '',
    allergies: '',
    medical_conditions: '',
    additional_notes: ''
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  // Load child data from children list
  useEffect(() => {
    const loadChild = async () => {
      try {
        setIsLoading(true);
        const children = await api.getChildren();
        const child = children.find(c => c.id === id);

        if (!child) {
          setError('Child not found.');
          return;
        }

        setFormData({
          first_name: child.first_name || '',
          last_name: child.last_name || '',
          date_of_birth: child.date_of_birth ? child.date_of_birth.split('T')[0] : '',
          gender: child.gender || '',
          grade: child.child_properties?.grade || '',
          allergies: child.child_properties?.allergies || '',
          medical_conditions: child.child_properties?.medical_conditions || '',
          additional_notes: child.child_properties?.additional_notes || ''
        });
        setError('');
      } catch (error) {
        console.error('Failed to load child:', error);
        setError(error.response?.data?.error || error.response?.data?.message || 'Failed to load child information. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      loadChild();
    }
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setError('');

    const submitData = {
      ...formData,
      grade: parseInt(formData.grade, 10)
    };

    try {
      await api.updateChild(id, submitData);
      navigate('/my-account');
    } catch (error) {
      console.error('Update child failed:', error);
      setError(error.response?.data?.error || error.response?.data?.message || 'Failed to update child information. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/my-account');
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default'
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading child information...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 4 }}>
      <Container maxWidth="md">
        <Button
          component={RouterLink}
          to="/my-account"
          startIcon={<ArrowBackIcon />}
          sx={{ mb: 3 }}
        >
          Back to My Account
        </Button>

        <Card>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h4" component="h2" sx={{ fontWeight: 700, mb: 1 }}>
              Edit Child Information
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Update your child's information below.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} noValidate>
              {/* Basic Information Section */}
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Basic Information
              </Typography>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  required
                  disabled={isSaving}
                  placeholder="Enter child's first name"
                />

                <TextField
                  fullWidth
                  label="Last Name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  required
                  disabled={isSaving}
                  placeholder="Enter child's last name"
                />
              </Box>

              <TextField
                fullWidth
                label="Date of Birth"
                name="date_of_birth"
                type="date"
                value={formData.date_of_birth}
                onChange={handleChange}
                required
                disabled={isSaving}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: new Date().toISOString().split('T')[0] }}
                sx={{ mb: 2 }}
              />

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 3 }}>
                <FormControl fullWidth required>
                  <InputLabel>Gender</InputLabel>
                  <Select
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    disabled={isSaving}
                    label="Gender"
                  >
                    <MenuItem value="Male">Male</MenuItem>
                    <MenuItem value="Female">Female</MenuItem>
                  </Select>
                </FormControl>

                <FormControl fullWidth required>
                  <InputLabel>Grade</InputLabel>
                  <Select
                    name="grade"
                    value={formData.grade}
                    onChange={handleChange}
                    disabled={isSaving}
                    label="Grade"
                  >
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((grade) => (
                      <MenuItem key={grade} value={grade}>
                        {grade}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Medical Information Section */}
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Medical Information
              </Typography>

              <TextField
                fullWidth
                label="Allergies"
                name="allergies"
                value={formData.allergies}
                onChange={handleChange}
                disabled={isSaving}
                placeholder="List any allergies (optional)"
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Medical Conditions"
                name="medical_conditions"
                value={formData.medical_conditions}
                onChange={handleChange}
                disabled={isSaving}
                placeholder="List any medical conditions (optional)"
                sx={{ mb: 2 }}
              />

              <Divider sx={{ my: 3 }} />

              {/* Additional Information Section */}
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Additional Information
              </Typography>

              <TextField
                fullWidth
                label="Additional Notes"
                name="additional_notes"
                value={formData.additional_notes}
                onChange={handleChange}
                disabled={isSaving}
                placeholder="Any additional information we should know (optional)"
                multiline
                rows={4}
                sx={{ mb: 3 }}
              />

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={handleCancel}
                  disabled={isSaving}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={
                    isSaving ||
                    !formData.first_name.trim() ||
                    !formData.last_name.trim() ||
                    !formData.date_of_birth ||
                    !formData.gender ||
                    !formData.grade
                  }
                  startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                >
                  {isSaving ? 'Updating...' : 'Update Child'}
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}

export default EditChild;
